import { ConferenceOrderItem, ConferenceOrderFilterPayload } from '../SellerQueue/types'
import {
  filterAdminQueueSellerReportOrders,
  filterTotvsReportOrders,
  getUsersByTypeRoute,
} from '../SellerQueue/services'
import {
  SellerTicketAverageRow,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from './types'
import { ConferenceSeller } from '../SellerQueue/types'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const readString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const normalizeCpf = (value: string): string => value.replace(/\D/g, '')

const normalizeSellerCode = (value: string): string => value.trim().toUpperCase()

const safeString = (value?: string | null): string => (value || '').trim()

const getOrderSource = (order: ConferenceOrderItem): 'ninegrid' | 'totvs' | 'unknown' => {
  if (order.source === 'ninegrid' || order.source === 'totvs') {
    return order.source
  }

  return 'unknown'
}

const getSellerKeyAndLabel = (
  order: ConferenceOrderItem
): {
  sellerKey: string
  sellerLabel: string
  sellerCode: string
  sellerCpf: string
} => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const rawNinegridOrder = isRecord(content.rawNinegridOrder) ? content.rawNinegridOrder : {}
  const commissioneds = Array.isArray(rawTotvsOrder.commissioneds) ? rawTotvsOrder.commissioneds : []
  const firstCommissioned = isRecord(commissioneds[0]) ? commissioneds[0] : {}

  const sellerNameCandidates = [
    content.sellerName,
    rawNinegridOrder.sellerName,
    rawTotvsOrder.sellerName,
    firstCommissioned.sellerName,
    rawTotvsOrder.representativeName,
  ]

  const sellerCodeCandidates = [
    content.sellerCode,
    rawNinegridOrder.sellerCode,
    rawTotvsOrder.sellerCode,
    firstCommissioned.sellerCode,
    rawTotvsOrder.representativeCode,
  ]

  const sellerCpfCandidates = [
    content.sellerCpf,
    rawNinegridOrder.sellerCpf,
    rawTotvsOrder.sellerCpfCnpj,
    firstCommissioned.sellerCpfCnpj,
  ]

  const sellerName = sellerNameCandidates.map(readString).find(Boolean) || ''
  const sellerCode = sellerCodeCandidates.map(readString).find(Boolean) || ''
  const sellerCpf = sellerCpfCandidates.map(readString).find(Boolean) || ''

  const sellerKey = sellerCode || sellerCpf || sellerName || 'sem-vendedor'
  const sellerLabel = sellerName || sellerCode || sellerCpf || 'Sem vendedor'

  return {
    sellerKey,
    sellerLabel,
    sellerCode,
    sellerCpf,
  }
}

const getOrderTotalCents = (order: ConferenceOrderItem): number => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const counts = isRecord(rawTotvsOrder.counts) ? rawTotvsOrder.counts : {}

  const candidates = [
    order.total,
    content.total,
    rawTotvsOrder.total,
    rawTotvsOrder.totalValue,
    counts.totalValue,
    counts.grossValue,
    counts.netValue,
  ]

  for (const candidate of candidates) {
    const value = readNumber(candidate)
    if (value !== null) {
      return value > 1000 ? Math.round(value) : Math.round(value * 100)
    }
  }

  return 0
}

const normalizeOrders = (orders: ConferenceOrderItem[]): ConferenceOrderItem[] => orders

interface BuildSellerRow extends SellerTicketAverageRow {
  sellerCode?: string
  sellerCpf?: string
}

const formatSellerLabel = (sellerCode: string, sellerName: string, fallbackLabel: string): string => {
  if (sellerCode && sellerName) {
    return `${sellerCode} - ${sellerName}`
  }

  return sellerName || sellerCode || fallbackLabel
}

const buildSellerLookup = (users: ConferenceSeller[]): {
  byCode: Map<string, ConferenceSeller>
  byCpf: Map<string, ConferenceSeller>
} => {
  const byCode = new Map<string, ConferenceSeller>()
  const byCpf = new Map<string, ConferenceSeller>()

  users.forEach((user) => {
    const sellerCode = normalizeSellerCode(safeString(user.sellerCode))
    const sellerCpf = normalizeCpf(safeString(user.cpf))

    if (sellerCode) {
      byCode.set(sellerCode, user)
    }

    if (sellerCpf) {
      byCpf.set(sellerCpf, user)
    }
  })

  return { byCode, byCpf }
}

const enrichSellerRows = (rows: BuildSellerRow[], users: ConferenceSeller[]): SellerTicketAverageRow[] => {
  if (!users.length) {
    return rows
  }

  const { byCode, byCpf } = buildSellerLookup(users)

  return rows.map((row) => {
    const rowCode = normalizeSellerCode(safeString(row.sellerCode) || safeString(row.sellerKey))
    const rowCpf = normalizeCpf(safeString(row.sellerCpf) || safeString(row.sellerKey))

    const matchedUser = (rowCode ? byCode.get(rowCode) : undefined) || (rowCpf ? byCpf.get(rowCpf) : undefined)

    if (!matchedUser) {
      return {
        sellerKey: row.sellerKey,
        sellerLabel: row.sellerLabel,
        orderCount: row.orderCount,
        totalAmountCents: row.totalAmountCents,
        averageTicketCents: row.averageTicketCents,
      }
    }

    const userName = safeString(matchedUser.name)
    const userCode = safeString(matchedUser.sellerCode)
    const displayCode = row.sellerCode || userCode

    return {
      sellerKey: row.sellerKey,
      sellerLabel: formatSellerLabel(displayCode, userName, row.sellerLabel),
      orderCount: row.orderCount,
      totalAmountCents: row.totalAmountCents,
      averageTicketCents: row.averageTicketCents,
    }
  })
}

const buildRows = (orders: ConferenceOrderItem[]): BuildSellerRow[] => {
  const grouped = new Map<
    string,
    {
      sellerLabel: string
      sellerCode: string
      sellerCpf: string
      orderCount: number
      totalAmountCents: number
    }
  >()

  for (const order of orders) {
    const { sellerKey, sellerLabel, sellerCode, sellerCpf } = getSellerKeyAndLabel(order)
    const totalAmountCents = getOrderTotalCents(order)

    const current = grouped.get(sellerKey) || {
      sellerLabel,
      sellerCode,
      sellerCpf,
      orderCount: 0,
      totalAmountCents: 0,
    }

    current.sellerLabel = sellerLabel || current.sellerLabel
    current.sellerCode = current.sellerCode || sellerCode
    current.sellerCpf = current.sellerCpf || sellerCpf
    current.orderCount += 1
    current.totalAmountCents += totalAmountCents

    grouped.set(sellerKey, current)
  }

  return Array.from(grouped.entries())
    .map(([sellerKey, value]) => ({
      sellerKey,
      sellerLabel: value.sellerLabel,
      sellerCode: value.sellerCode,
      sellerCpf: value.sellerCpf,
      orderCount: value.orderCount,
      totalAmountCents: value.totalAmountCents,
      averageTicketCents:
        value.orderCount > 0 ? Math.round(value.totalAmountCents / value.orderCount) : 0,
    }))
    .sort((left, right) => right.totalAmountCents - left.totalAmountCents)
}

const buildSourceBreakdown = (orders: ConferenceOrderItem[]) => {
  const bySource = new Map<'ninegrid' | 'totvs', ConferenceOrderItem[]>()
  bySource.set('ninegrid', [])
  bySource.set('totvs', [])

  for (const order of orders) {
    const source = getOrderSource(order)
    if (source === 'unknown') {
      continue
    }

    bySource.get(source)?.push(order)
  }

  return (['ninegrid', 'totvs'] as const).map((source) => {
    const sourceOrders = bySource.get(source) || []
    const totalAmountCents = sourceOrders.reduce((accumulator, order) => accumulator + getOrderTotalCents(order), 0)

    return {
      source,
      orderCount: sourceOrders.length,
      totalAmountCents,
      averageTicketCents:
        sourceOrders.length > 0 ? Math.round(totalAmountCents / sourceOrders.length) : 0,
    }
  })
}

export const loadSellerTicketAverageReport = async (
  filters: SellerTicketAverageFilters
): Promise<SellerTicketAverageReport> => {
  const sellerUsersPromise = getUsersByTypeRoute('seller').catch(() => [])

  const payload: ConferenceOrderFilterPayload = {
    startDate: filters.startDate,
    finalDate: filters.finalDate,
  }

  const orders: ConferenceOrderItem[] = []

  if (filters.source === 'all' || filters.source === 'ninegrid') {
    const ninegridOrders = await filterAdminQueueSellerReportOrders(payload)
    orders.push(...ninegridOrders)
  }

  if (filters.source === 'all' || filters.source === 'totvs') {
    const totvsResult = await filterTotvsReportOrders(payload)
    orders.push(...totvsResult.items)
  }

  const normalizedOrders = normalizeOrders(orders)
  const totalAmountCents = normalizedOrders.reduce(
    (accumulator, order) => accumulator + getOrderTotalCents(order),
    0
  )
  const rows = buildRows(normalizedOrders)
  const sellerUsers = await sellerUsersPromise
  const enrichedRows = enrichSellerRows(rows, sellerUsers)

  return {
    summary: {
      totalOrders: normalizedOrders.length,
      totalAmountCents,
      averageTicketCents:
        normalizedOrders.length > 0 ? Math.round(totalAmountCents / normalizedOrders.length) : 0,
    },
    rows: enrichedRows,
    sourceBreakdown: buildSourceBreakdown(normalizedOrders),
    items: normalizedOrders,
  }
}